import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RequireAuth } from "@/components/auth/RouteGuards";
import { RequireAnyPermission, RequirePermission } from "@/components/auth/RequirePermission";
import { AuthProvider } from "@/hooks/useAuth";
import { platformSupabaseClient } from "@/adapters/platformSupabaseClient";
import { createScheduleSelfServiceSupabasePort } from "@tihany/module-schedule-adapter-supabase";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";

const PublicHome = lazy(() => import("./pages/PublicHome"));
const Home = lazy(() => import("./pages/Home"));
const InternalModuleGuide = lazy(() => import("./pages/InternalModuleGuide"));
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const Updates = lazy(() => import("./pages/Updates"));
const AISettings = lazy(() => import("./pages/AISettings"));
const ScheduleBuilder = lazy(() => import("./features/schedule-builder/ScheduleBuilderPage"));
const MySchedule = lazy(() => import("./features/my-schedule/MySchedulePage"));
const ManageStaff = lazy(() => import("./pages/ManageStaff"));
const Notifications = lazy(() => import("./pages/Notifications"));
const LegalDocument = lazy(() => import("./pages/LegalDocument"));
const InternalGdprApproval = lazy(() => import("./pages/InternalGdprApproval"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();
const appSchedulePort = createScheduleSelfServiceSupabasePort(platformSupabaseClient);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
            <Routes>
              <Route path="/" element={<PublicHome />} />
              <Route path="/staff" element={<RequirePermission permission="workspace.view"><Home schedulePort={appSchedulePort} /></RequirePermission>} />
              <Route path="/staff/guide" element={<RequirePermission permission="workspace.view"><InternalModuleGuide /></RequirePermission>} />
              <Route path="/counter" element={<RequirePermission permission="counter.view"><Index /></RequirePermission>} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<RequireAuth><Navigate to="/staff" replace /></RequireAuth>} />
              <Route path="/dashboard" element={<RequirePermission permission="dashboard.view"><Dashboard /></RequirePermission>} />
              <Route
                path="/dashboard/knowledge"
                element={
                  <RequirePermission permission="ai_center.view">
                    <RequirePermission permission="ai.knowledge.manage">
                      <Knowledge />
                    </RequirePermission>
                  </RequirePermission>
                }
              />
              <Route
                path="/dashboard/updates"
                element={
                  <RequirePermission permission="ai_center.view">
                    <RequirePermission permission="ai.updates.manage">
                      <Updates />
                    </RequirePermission>
                  </RequirePermission>
                }
              />
              <Route path="/dashboard/ai-settings" element={<RequirePermission permission="ai_center.view"><AISettings /></RequirePermission>} />
              <Route
                path="/schedule-builder"
                element={<RequirePermission permission="schedule_builder.view"><ScheduleBuilder schedulePort={appSchedulePort} /></RequirePermission>}
              />
              <Route
                path="/my-schedule"
                element={<RequirePermission permission="my_schedule.view"><MySchedule schedulePort={appSchedulePort} /></RequirePermission>}
              />
              <Route
                path="/notifications"
                element={(
                  <RequireAnyPermission permissions={["workspace.view", "my_schedule.notifications.view"]}>
                    <Notifications />
                  </RequireAnyPermission>
                )}
              />
              <Route path="/admin/staff" element={<RequirePermission permission="admin.staff.view"><ManageStaff /></RequirePermission>} />
              <Route path="/internal/gdpr-approval" element={<RequireAuth skipInternalPolicyCheck><InternalGdprApproval /></RequireAuth>} />
              <Route path="/legal/privacy" element={<LegalDocument docType="privacy" />} />
              <Route path="/legal/cookies" element={<LegalDocument docType="cookies" />} />
              <Route path="/legal/gdpr" element={<LegalDocument docType="gdpr" />} />
              <Route path="/legal/aszf" element={<LegalDocument docType="aszf" />} />
              <Route
                path="/legal/internal"
                element={<RequireAuth skipInternalPolicyCheck><LegalDocument docType="internal" /></RequireAuth>}
              />
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
